import { ApplicationErrorCode } from '@oysterjs/core/graphql';
import type { ActionResult } from '@oysterjs/core/graphql/actions';
import {
  SmartFillAnswerSource,
  SmartFillFieldType,
  type AnswerSmartFillFormBucketMutation,
  type SmartFillFormAnswerInput,
  type SmartFillFormBucketFieldsFragment,
  type SmartFillFormBucketQuestionsFieldsFragment,
  type SmartFillCarrierSiteFieldsFragment,
  type SmartFillFormQuestionFieldsFragment
} from '~/api/graphql';

type CarrierSite = Pick<SmartFillCarrierSiteFieldsFragment, 'id' | 'carrier' | 'product'>;
type Question = SmartFillFormQuestionFieldsFragment;
type FactRef = Pick<Question, 'ownerType' | 'relationPath' | 'semanticKey'>;

export const carrierSiteName = (site: Pick<CarrierSite, 'carrier' | 'product'>) =>
  `${site.carrier} · ${site.product}`;

/**
 * The insured, for a bucket still named the way it was built ("<insured> · <line of
 * business>"). A bucket renamed since keeps the name it was given.
 */
export const bucketTitle = ({
  bucket,
  lineOfBusiness
}: Pick<SmartFillFormBucketFieldsFragment, 'bucket' | 'lineOfBusiness'>) => {
  const suffix = ` · ${lineOfBusiness}`;
  return bucket.name.length > suffix.length && bucket.name.endsWith(suffix)
    ? bucket.name.slice(0, -suffix.length)
    : bucket.name;
};

/** What the bucket was built from: its forms, such as "ACORD 125 v2 and ACORD 126", or its workspace. */
export const bucketSources = ({ forms }: Pick<SmartFillFormBucketFieldsFragment, 'forms'>) => {
  if (forms.length === 0) {
    return 'the workspace';
  }
  const titles = forms.map((form) =>
    form.version > 1 ? `${form.title} v${form.version}` : form.title
  );
  return titles.length === 1
    ? titles[0]
    : `${titles.slice(0, -1).join(', ')} and ${titles[titles.length - 1]}`;
};

/** The sites selling the bucket's line of business first, then every other recorded site. */
export const siteGroups = <Site extends CarrierSite>(sites: Site[], lineOfBusiness: string) => {
  const forLine = (site: Site) =>
    site.product.trim().toLowerCase() === lineOfBusiness.trim().toLowerCase();
  return { forLine: sites.filter(forLine), others: sites.filter((site) => !forLine(site)) };
};

export type QuestionStatus = 'bucket' | 'agent' | 'needed';

/**
 * A question is needed until every record answers it, and is the agent's once any
 * record holds an answer they typed: the bucket alone did not finish it.
 */
export const questionStatus = ({ answers }: Pick<Question, 'answers'>): QuestionStatus => {
  const sources = answers.map((answer) => answer.source);
  if (sources.length === 0 || sources.includes(SmartFillAnswerSource.Missing)) {
    return 'needed';
  }
  return sources.includes(SmartFillAnswerSource.Agent) ? 'agent' : 'bucket';
};

export type CoverageCounts = Record<QuestionStatus, number> & { total: number };

export const coverageCounts = (questions: Pick<Question, 'answers'>[]): CoverageCounts => {
  const counts: CoverageCounts = { bucket: 0, agent: 0, needed: 0, total: questions.length };
  for (const question of questions) {
    counts[questionStatus(question)] += 1;
  }
  return counts;
};

export const OTHER_SECTION = 'Other';

export type QuestionSection = {
  name: string;
  questions: { question: Question; index: number }[];
};

/**
 * Sections in the order the site reaches them, each keeping the site's order and
 * each question's position in it. Questions with no section come last.
 */
export const questionSections = (
  questions: Question[],
  include: (question: Question) => boolean = () => true
): QuestionSection[] => {
  const sections = new Map<string, QuestionSection>();
  questions.forEach((question, index) => {
    if (!include(question)) {
      return;
    }
    const name = question.section?.trim() || OTHER_SECTION;
    const section = sections.get(name) ?? { name, questions: [] };
    section.questions.push({ question, index });
    sections.set(name, section);
  });
  const ordered = [...sections.values()];
  return [
    ...ordered.filter((section) => section.name !== OTHER_SECTION),
    ...ordered.filter((section) => section.name === OTHER_SECTION)
  ];
};

/** Repeated records -- drivers, vehicles, locations -- sit below the bucket's root. */
export const isRecordQuestion = (question: Pick<Question, 'relationPath'>) =>
  question.relationPath.trim() !== '';

/** One record's answer to one fact, matched without regard to case as the server matches it. */
export const answerKey = (fact: FactRef, recordIndex: number) =>
  JSON.stringify([
    fact.ownerType.trim().toLowerCase(),
    fact.relationPath.trim().toLowerCase(),
    fact.semanticKey.trim().toLowerCase(),
    recordIndex
  ]);

export type AnswerControl =
  | { type: 'yes-no' }
  | { type: 'select'; options: string[] }
  | { type: 'date' | 'number' | 'tel' | 'email' | 'textarea' | 'text' };

// Recorded option lists keep the prompt a site shows before anything is chosen.
const PLACEHOLDER_OPTION = /^[\s\-–—]*(please\s+)?(choose|select)\b|^\{[^}]*\}$/i;

const siteOptions = (options: string[]) => [
  ...new Set(
    options
      .map((option) => option.trim())
      .filter((option) => option && !PLACEHOLDER_OPTION.test(option))
  )
];

export const answerControl = (
  question: Pick<Question, 'kind' | 'fieldType' | 'options'>
): AnswerControl => {
  const kind = question.kind.toUpperCase();
  const either = (valueKind: string, fieldType: SmartFillFieldType) =>
    kind === valueKind || question.fieldType === fieldType;
  if (either('BOOLEAN', SmartFillFieldType.Checkbox)) {
    return { type: 'yes-no' };
  }
  const options = siteOptions(question.options);
  if (options.length > 0) {
    return { type: 'select', options };
  }
  if (either('DATE', SmartFillFieldType.Date)) {
    return { type: 'date' };
  }
  if (either('NUMBER', SmartFillFieldType.Number) || kind === 'MONEY') {
    return { type: 'number' };
  }
  if (either('PHONE', SmartFillFieldType.Phone)) {
    return { type: 'tel' };
  }
  if (either('EMAIL', SmartFillFieldType.Email)) {
    return { type: 'email' };
  }
  return { type: question.fieldType === SmartFillFieldType.Textarea ? 'textarea' : 'text' };
};

// The spellings the server accepts for a boolean.
const YES = new Set(['true', 'yes', 'y', '1', 'checked']);
const NO = new Set(['false', 'no', 'n', '0', 'unchecked']);
const US_DATE = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/;
const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/;

/**
 * A stored value the way its control shows and writes it: Yes or No, YYYY-MM-DD, or
 * bare digits. Comparing drafts in this form keeps a value the agent left alone from
 * counting as a change just because the bucket spells it differently.
 */
export const controlValue = (control: AnswerControl, value: string | null | undefined): string => {
  const stored = (value ?? '').trim();
  switch (control.type) {
    case 'yes-no': {
      const spelling = stored.toLowerCase();
      return YES.has(spelling) ? 'Yes' : NO.has(spelling) ? 'No' : stored;
    }
    case 'date': {
      const us = US_DATE.exec(stored);
      return us ? `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}` : stored;
    }
    case 'number': {
      const digits = stored.replace(/[$,\s]/g, '');
      return PLAIN_NUMBER.test(digits) ? digits : stored;
    }
    default:
      return stored;
  }
};

/** A choice control's options, plus the current value when the site never offered it. */
export const controlChoices = (control: AnswerControl, current: string): string[] => {
  const options =
    control.type === 'yes-no' ? ['Yes', 'No'] : control.type === 'select' ? control.options : [];
  return current && !options.includes(current) ? [current, ...options] : options;
};

/** What the agent has typed, by answerKey, whether or not it differs from the bucket. */
export type AnswerDrafts = Readonly<Record<string, string>>;

/**
 * The drafts that differ from what their record holds, as the mutation takes them.
 * What the bucket was built with is read-only; an emptied answer of the agent's goes
 * as an empty value, which clears it.
 */
export const changedAnswers = (
  questions: Question[],
  drafts: AnswerDrafts
): SmartFillFormAnswerInput[] =>
  questions.flatMap((question) => {
    const control = answerControl(question);
    return question.answers.flatMap((answer) => {
      const draft = drafts[answerKey(question, answer.recordIndex)];
      if (answer.source === SmartFillAnswerSource.Bucket || draft === undefined) {
        return [];
      }
      const value = draft.trim();
      if (value === controlValue(control, answer.value)) {
        return [];
      }
      return [
        {
          ownerType: question.ownerType,
          relationPath: question.relationPath,
          semanticKey: question.semanticKey,
          recordIndex: answer.recordIndex,
          value
        }
      ];
    });
  });

/** The drafts a save leaves: any edited again while it was in flight still differ. */
export const unsavedDrafts = (
  drafts: AnswerDrafts,
  saved: SmartFillFormAnswerInput[]
): AnswerDrafts => {
  const remaining = { ...drafts };
  for (const answer of saved) {
    const key = answerKey(answer, answer.recordIndex);
    if (remaining[key]?.trim() === answer.value) {
      delete remaining[key];
    }
  }
  return remaining;
};

export type SaveFailure = { message: string; conflict: boolean };

export type SaveOutcome =
  | { saved: SmartFillFormBucketQuestionsFieldsFragment }
  | { failure: SaveFailure };

/** The refreshed questions a save returned, or what to tell the agent about its failure. */
export const saveOutcome = (
  result: ActionResult<AnswerSmartFillFormBucketMutation>
): SaveOutcome => {
  const saved = result?.data?.answerSmartFillFormBucket;
  if (saved) {
    return { saved };
  }
  const applicationError = result?.applicationError;
  switch (applicationError?.code) {
    case ApplicationErrorCode.Conflict:
      return {
        failure: {
          conflict: true,
          message: 'This bucket changed since you opened it, so it has been reloaded. Save again.'
        }
      };
    case ApplicationErrorCode.Forbidden:
      return {
        failure: {
          conflict: false,
          message: "Smart Fill buckets aren't turned on for your agency."
        }
      };
  }
  const message = applicationError?.message ?? result?.validationErrors?.[0]?.message;
  return {
    failure: { conflict: false, message: message || "Your answers didn't save. Try again." }
  };
};
